/**
 * Active entity (tenant) on the client: Vector, Kairos, …
 *
 * The slug is only a *preference*. Every request sends it as X-RiskLo-Entity and
 * the server validates it against the entities the caller provably belongs to
 * (see middleware/entityContext.js), so a tampered value can never widen access
 * — it just falls back to the caller's default brand.
 */

import { applyBrandTheme, applyBrandLayout } from './brandTheme';

const ACTIVE_ENTITY_KEY = 'risklo_active_entity';
export const ENTITY_HEADER = 'X-RiskLo-Entity';
export const ENTITY_CHANGED_EVENT = 'risklo-active-entity-changed';
// The cached brand's look changed (name, logo, icon): repaint the shell in place.
// Unlike ENTITY_CHANGED_EVENT this never reloads the page.
export const BRAND_IDENTITY_EVENT = 'risklo-brand-identity-changed';

function announceBrandIdentity() {
  try { window.dispatchEvent(new CustomEvent(BRAND_IDENTITY_EVENT)); } catch { /* old browsers */ }
}

export function getActiveEntitySlug() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const value = String(localStorage.getItem(ACTIVE_ENTITY_KEY) || '').trim().toLowerCase();
    return value || null;
  } catch {
    return null;
  }
}

export function setActiveEntitySlug(slug) {
  const value = String(slug || '').trim().toLowerCase();
  try {
    if (value) localStorage.setItem(ACTIVE_ENTITY_KEY, value);
    else localStorage.removeItem(ACTIVE_ENTITY_KEY);
  } catch { /* private browsing — header just won't be sent */ }
  try {
    window.dispatchEvent(new CustomEvent(ENTITY_CHANGED_EVENT, { detail: { slug: value || null } }));
  } catch { /* no window (SSR/tests) */ }
}

/** Header object to spread into any fetch that returns entity-scoped data. */
export function getEntityHeaders() {
  const slug = getActiveEntitySlug();
  return slug ? { [ENTITY_HEADER]: slug } : {};
}

/**
 * Cached label + branding for the active entity, so nav can read it
 * synchronously without every consumer refetching /api/entities/me.
 * Written by EntitySwitcher after it loads.
 */
const ENTITY_BRAND_KEY = 'risklo_active_entity_brand';

/*
 * Self-use preview (Account → Preview as → Self-use Basic/Pro/Max): a Platform
 * Admin sees the app as someone with no company. The company brand is still
 * cached, but nothing reads it while the preview is on: the RiskLo home look,
 * name and logo show, and brand labels fall back to their no-brand wording.
 * Set by AuthContext from the admin's preview choice.
 */
let selfUsePreview = false;

export function isSelfUsePreviewActive() {
  return selfUsePreview;
}

export function setSelfUsePreview(on) {
  const next = Boolean(on);
  if (next === selfUsePreview) return;
  selfUsePreview = next;
  const brand = next ? null : getActiveEntityBrand();
  applyBrandTheme(brand?.branding?.theme || null);
  applyBrandLayout(brand?.branding?.layout || null);
  announceBrandIdentity();
}

export function setActiveEntityBrand(entity) {
  // The brand's colors and layout style (Admin → Brands) follow the active brand.
  if (!selfUsePreview) {
    applyBrandTheme(entity?.branding?.theme || null);
    applyBrandLayout(entity?.branding?.layout || null);
  }
  try {
    if (!entity) localStorage.removeItem(ENTITY_BRAND_KEY);
    else localStorage.setItem(ENTITY_BRAND_KEY, JSON.stringify({
      slug: entity.slug,
      displayName: entity.displayName,
      branding: entity.branding || {},
    }));
  } catch { /* private browsing */ }
  announceBrandIdentity();
}

export function getActiveEntityBrand() {
  if (selfUsePreview) return null;
  try {
    const raw = localStorage.getItem(ENTITY_BRAND_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Forget the active brand entirely. Must be called on SIGN-OUT.
 *
 * The slug and the cached branding are per-USER preferences, but they live in
 * localStorage, which is per-BROWSER. Leaving them behind meant the next person
 * to sign in started inside the previous user's brand: the server correctly
 * refused to serve that brand's data (the header is validated against provable
 * access), but every label read from this cache — the "<Brand> Results" nav
 * item, the dashboard heading, the data-source note — still announced a brand
 * the new user has no access to. Data was right, identity was wrong, and it
 * looked exactly like an isolation leak.
 *
 * Deliberately SILENT — it must not fire ENTITY_CHANGED_EVENT.
 *
 * That event means "the same signed-in user switched brand, refetch everything",
 * and App.js implements it as a full window.location.reload(). Clearing is a
 * different thing: the session is going away, so there is nothing to refetch.
 * Broadcasting it here caused an unbounded reload loop — sign-out or an
 * entity-less /api/entities/me cleared, the clear reloaded, the reload cleared
 * again. Use setActiveEntitySlug() when a live app genuinely changes brand.
 */
export function clearActiveEntity() {
  applyBrandTheme(null);
  applyBrandLayout(null);
  try {
    localStorage.removeItem(ACTIVE_ENTITY_KEY);
    localStorage.removeItem(ENTITY_BRAND_KEY);
  } catch { /* private browsing — nothing was stored anyway */ }
  announceBrandIdentity();
}

/**
 * A brand's sign-in link (risklo.io/b/<slug> or ?brand=<slug>) picked this brand
 * before anyone signed in. Stored without ENTITY_CHANGED_EVENT (that reloads the
 * page); the server still only serves a brand the user really has access to.
 */
export function rememberEntryEntitySlug(slug) {
  const value = String(slug || '').trim().toLowerCase();
  if (!value) return;
  try { localStorage.setItem(ACTIVE_ENTITY_KEY, value); } catch { /* private browsing */ }
}

/**
 * Label for the "<Brand> Results" nav item. Never names another brand: with no
 * brand known yet it reads "Strategy Results".
 */
export function getResultsNavLabel() {
  const brand = currentBrand();
  const explicit = brand?.branding?.resultsLabel;
  if (explicit) return String(explicit);
  return `${getEntityDisplayName()} Results`;
}

export const DEFAULT_ENTITY_SLUG = 'vector';

/**
 * The cached brand, but only when it belongs to the active slug. A cache left
 * over from another brand must never label this one.
 */
export function getCurrentBrand() {
  return currentBrand();
}

function currentBrand() {
  const brand = getActiveEntityBrand();
  if (!brand) return null;
  const slug = getActiveEntitySlug();
  const brandSlug = brand.slug ? String(brand.slug).trim().toLowerCase() : '';
  if (slug && brandSlug && slug !== brandSlug) return null;
  return brand;
}

/**
 * The active brand's name, for copy that used to say "Vector" unconditionally.
 *
 * One brand's name must never appear on another brand, so when the brand is
 * not known yet (first paint, stale cache) this returns `fallback`, a neutral
 * word, rather than guessing. "Vector" only comes back when the active slug
 * really is Vector.
 */
export function getEntityDisplayName(fallback = 'Strategy') {
  const brand = currentBrand();
  const name = brand?.displayName ? String(brand.displayName).trim() : '';
  if (name) return name;
  return getActiveEntitySlug() === DEFAULT_ENTITY_SLUG ? 'Vector' : fallback;
}

/**
 * True only for Vector Capital. Some features are Vector's own products rather
 * than generic capabilities — the Equity Portfolios (five hardcoded tickers on
 * Vector's 1H algo curves) and the legacy hardcoded scenario pills — and those
 * are hidden for every other brand. The server enforces the same rule; this
 * only keeps the UI from rendering an empty shell.
 */
export function isVectorEntity() {
  const slug = getActiveEntitySlug();
  // No slug yet (first paint, single-tenant) behaves as Vector, matching the
  // server's narrowing default.
  return !slug || slug === DEFAULT_ENTITY_SLUG;
}

const THIRD_PARTY_NOTE = 'Third-party strategy data is for analytics only and may be '
  + 'historical, simulated, or model-based, not recommendations or guarantees.';

/**
 * Where the numbers on screen come from. Only Vector reads the Google Results
 * Spreadsheet — it is Vector Capital's own document, not shared infrastructure.
 * Every other brand is populated through RiskLo itself (Strategy Data / Sheet
 * Manager), so telling its users about a Google sheet would be wrong.
 *
 * @param {{withDisclaimer?: boolean}} opts append the third-party data note
 */
export function getDataSourceNote({ withDisclaimer = true } = {}) {
  const base = isVectorEntity()
    ? 'Data updates automatically from the Google Results Spreadsheet.'
    : 'Data comes from the strategies uploaded in Strategy Data.';
  return withDisclaimer ? `${base} ${THIRD_PARTY_NOTE}` : base;
}
